import {useState} from "react";
import "../App.css";
import {Helmet} from "react-helmet";
import "../CSS/programmes/OrderProcessing.css";

import {DefaultButton, SubmitButton} from "../components/Buttons.tsx";
import Navbar from "../components/Navbar.tsx";
import {ColorModeIcon} from "../context/ColorModeContext.tsx";
import ArticleSelectionDialog, {Article} from "../components/ArticleSelectionDialog.tsx";


import * as React from 'react';
import CustomTextField from "../components/CustomTextField.tsx";

import {RadioGroup} from "@mui/material";
import CustomCheckbox from "../components/CustomCheckbox.tsx";
import axios from "axios";
import {createRoot} from "react-dom/client";

/**
 * Example modal dialog. Replace with your UI framework (MUI, AntD, etc.)
 * Returns a Promise<boolean>.
 */
function showConfirmationDialog(message) {
  return new Promise((resolve) => {
    const confirm = window.confirm(message + "\n (OK = Weitermachen, Cancel = Anfrage Abbrechen)");
    resolve(confirm);
  });
}

function showArticleSelectionDialog(articles: Article[]): Promise<Article[] | null> {
  return new Promise((resolve) => {
    const div = document.createElement("div");
    document.body.appendChild(div);
    const root = createRoot(div);
    const cleanup = () => {
      root.unmount();
      div.remove();
    };

    root.render(
      <ArticleSelectionDialog
        articles={articles}
        onConfirm={(selectedArticles) => {
          cleanup();
          resolve(selectedArticles);
        }}
        onCancel={() => {
          cleanup();
          resolve(null);
        }}
      />
    );
  });
}

/**
 * Parse filename from a Content-Disposition header value.
 * Returns null if no filename can be extracted.
 * @param {string|undefined} disposition The Content-Disposition header value.
 * @returns {string|null}
 */
function getFilenameFromContentDisposition(disposition) {
  if (!disposition) return null;
  // RFC5987: filename*=UTF-8''encoded
  const filenameStarMatch = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (filenameStarMatch) {
    try {
      return decodeURIComponent(filenameStarMatch[1]);
    } catch {
      return filenameStarMatch[1];
    }
  }
  // filename="..."
  const filenameQuotedMatch = /filename="([^"]+)"/i.exec(disposition);
  if (filenameQuotedMatch) return filenameQuotedMatch[1];
  // filename=unquoted
  const filenameUnquotedMatch = /filename=([^;]+)/i.exec(disposition);
  if (filenameUnquotedMatch) return filenameUnquotedMatch[1].trim();
  return null;
}

function OrderProcessing() {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  // States for mandatory information
  const [selectedLager, setSelectedLager] = useState<string | null>(null);
  const [orderID, setOrderID] = useState<string | null>(null);
  const [infoMessage, setinfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);

  const handleOrderIDChange = (element: React.ChangeEvent<HTMLInputElement>) => {
    setOrderID(element.target.value);
  };
  const renderDefaultOptions = () => {
    return (
      <div>
        <div className="checkbox-div">
          <RadioGroup
            row
            value={selectedLager}
            onChange={(_, newValue) => setSelectedLager(newValue)} // standard RadioGroup pattern
          >
            <CustomCheckbox label="Dropshipping" value={selectedLager === "Dropshipping"}
                            onChange={() => setSelectedLager("Dropshipping")}/>
            <CustomCheckbox label="Kommissionslager" value={selectedLager === "Kommissionslager"}
                            onChange={() => setSelectedLager("Kommissionslager")}/>
            <CustomCheckbox label="Lagerartikel" value={selectedLager === "Lagerartikel"}
                            onChange={() => setSelectedLager("Lagerartikel")}/>
          </RadioGroup>
        </div>
        <div className="textfield-div">
          <CustomTextField
            type="text"
            label="Auftrags-ID"
            placeholder="Auftrags-ID"
            is_required={true}
            errorText="Dieses Feld muss ausgefüllt sein"
            value={orderID}
            onChange={handleOrderIDChange}
          />
        </div>
      </div>
    );
  }

  // ------------------------------------------------ Error Message and Info Message ------------------------------------------------
  const renderErrorMessage = () => {
    if (errorMessage) {
      return (
        <div className="error-message-container">
          {" "}
          <p>{errorMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  const renderInfoMessage = () => {
    if (infoMessage) {
      return (
        <div className="info-message-container">
          {" "}
          <p>{infoMessage}</p>{" "}
        </div>
      );
    } else {
      return null;
    }
  };

  /**
   * Calls the backend and automatically handles interactive "continue/abort" flows.
   * @param {number} orderID - ID of the order to process
   */
  async function processLagerOrder(orderID) {
    let orderData = null;
    let skipTelephoneNumberWarning = false;
    let skipLagerWarning = false;

    for (let attempt = 0; attempt <= 2; attempt++) {
      const dataLager = {
        orderID,
        skipTelephoneNumberWarning,
        skipLagerWarning,
        orderData,
      };

      try {
        const res = await axios.post(
          `${backendUrl}/order_processing/process_lager_article_order/`,
          dataLager,
          {...config, responseType: "blob"}
        );


        // in future: trigger file download set PDFURL
        const blob = new Blob([res.data], {type: "application/pdf"});
        const url = URL.createObjectURL(blob); // gpt proposed this: window.URL.createObjectURL(blob);

        // Try to determine filename from Content-Disposition header
        const disposition = res.headers["content-disposition"];
        const filenameFromHeader = getFilenameFromContentDisposition(disposition);
        // Fallback filename if header missing
        const fallbackName = `$AuftragsID {orderID}.pdf`;
        const filename = filenameFromHeader || fallbackName;

        const orderProcessingError = res.headers["x-order-processing-error"]; // header keys are lowercase in axios
        if (orderProcessingError) {
          setErrorMessage(
            "PDF wurde erstellt. Bitte drucke sie 2 mal aus. Es sind allerdings Fehler aufgetreten, welche deine Aktionen erfordern. Fehler: " + orderProcessingError
          );
        } else {
          setinfoMessage("Auftrag wurde erfolgreich bearbeitet. \n Bitte drucke die PDF 2 mal aus. ");
        }
        return { url, filename };

      } catch (err) {
        if (err.response) {
          // Error -> err.response.data is a Blob containing JSON
          const text = await err.response.data.text();
          const json = JSON.parse(text);

          // Check for the 2 built-in warnings and give user the option to abort
          if (err.response.status === 428) {
            const {error, message, order_data} = json;
            orderData = order_data;

            const continueAnyway = await showConfirmationDialog(message);
            if (!continueAnyway) {
              setinfoMessage("Auftrag wurde abgebrochen.");
              return null
            }

            if (error === "MISSING_PHONE") {
              skipTelephoneNumberWarning = true;
              continue; // retry
            }
            if (error === "NO_STOCK") {
              skipLagerWarning = true;
              continue; // retry
            }
          }

          // Actual errors
          const error_str = err.response.status === 500
            ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : json.detail || "Unbekannter Fehler";

          setErrorMessage(error_str);
          return null;
        } else {
          // Network errors
          setErrorMessage(
            "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
          );
          return null;
        }
      }
    }

    setErrorMessage(
      "Zu viele Versuche. Bitte notiere die OrderID und leite sie an Jonathan weiter."
    );
    return null;
  }

  async function processDropshipOrder(orderID) {
    let orderData = null;
    let skipTelephoneNumberWarning = false;
    let skipLagerWarning = false;

    for (let attempt = 0; attempt <= 2; attempt++) {
      const dataLager = {
        orderID,
        skipTelephoneNumberWarning,
        skipLagerWarning,
        orderData,
      };

      try {
        const res = await axios.post(
          `${backendUrl}/order_processing/process_dropship_order/`,
          dataLager,
          {...config, responseType: "blob"}
        );


        // in future: trigger file download set PDFURL
        // in future: trigger file download set PDFURL
        const blob = new Blob([res.data], {type: "application/pdf"});
        const url = URL.createObjectURL(blob); // gpt proposed this: window.URL.createObjectURL(blob);

        // Try to determine filename from Content-Disposition header
        const disposition = res.headers["content-disposition"];
        const filenameFromHeader = getFilenameFromContentDisposition(disposition);
        // Fallback filename if header missing
        const fallbackName = `$AuftragsID {orderID}.pdf`;
        const filename = filenameFromHeader || fallbackName;

        // Check for custom header
        const orderProcessingError = res.headers["x-order-processing-error"]; // header keys are lowercase in axios
        if (orderProcessingError) {
          setErrorMessage(
            "PDF wurde erstellt, aber es gab Fehler, welche deine Aktionen erfordern. Fehler: " + orderProcessingError
          );
        } else {
          setinfoMessage("Auftrag wurde erfolgreich bearbeitet.");
        }
        return { url, filename };

      } catch (err) {
        if (err.response) {
          // Error -> err.response.data is a Blob containing JSON
          const text = await err.response.data.text();
          const json = JSON.parse(text);

          // Check for the 2 built-in warnings and give user the option to abort
          if (err.response.status === 428) {
            const {error, message, order_data} = json;
            orderData = order_data;

            const continueAnyway = await showConfirmationDialog(message);
            if (!continueAnyway) {
              setinfoMessage("Auftrag wurde abgebrochen.");
              return null
            }

            if (error === "MISSING_PHONE") {
              skipTelephoneNumberWarning = true;
              continue; // retry
            }
            if (error === "NO_STOCK") {
              skipLagerWarning = true;
              continue; // retry
            }
          }

          // Actual errors
          const error_str = err.response.status === 500
            ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : json.detail || "Unbekannter Fehler";

          setErrorMessage(error_str);
          return null;
        } else {
          // Network errors
          setErrorMessage(
            "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
          );
          return null;
        }
      }
    }

    setErrorMessage(
      "Zu viele Versuche. Bitte notiere die OrderID und leite sie an Jonathan weiter."
    );
    return null;
  }

  async function processKommissionsOrder(orderID) {
    let orderData = null;
    let skipMultipleArticlesWarning = false;
    let skipLagerWarning = false;

    for (let attempt = 0; attempt <= 2; attempt++) {
      const dataLager = {
        orderID,
        skipMultipleArticlesWarning,
        skipLagerWarning,
        orderData,
      };

      try {
        const res = await axios.post(
          `${backendUrl}/order_processing/process_kommission_article_order/`,
          dataLager,
          {...config, responseType: "blob"}
        );


        // in future: trigger file download set PDFURL
        const contentType = res.headers["content-type"] || "application/zip";
        const blob = new Blob([res.data], {type: contentType});
        const url = URL.createObjectURL(blob); // gpt proposed this: window.URL.createObjectURL(blob);

        // Try to determine filename from Content-Disposition header
        const disposition = res.headers["content-disposition"];
        const filenameFromHeader = getFilenameFromContentDisposition(disposition);
        // Fallback filename if header missing
        const fallbackName = `$AuftragsID {orderID}.zip`;
        const filename = filenameFromHeader || fallbackName;

        // Check for custom header
        const orderProcessingError = res.headers["x-order-processing-error"]; // header keys are lowercase in axios
        if (orderProcessingError) {
          setErrorMessage(
            "PDF wurde erstellt, aber es gab Fehler, welche deine Aktionen erfordern. Fehler: " + orderProcessingError
          );
        } else {
          setinfoMessage("Auftrag wurde erfolgreich bearbeitet.");
        }
        return { url, filename };

      } catch (err) {
        if (err.response) {
          // Error -> err.response.data is a Blob containing JSON
          const text = await err.response.data.text();
          const json = JSON.parse(text);

          // Check for the 2 built-in warnings and give user the option to abort
          if (err.response.status === 428) {
            const {error, message, order_data} = json;
            orderData = order_data;

            if (error === "MULTIPLE_ARTICLES") {
              const selectedArticles = await showArticleSelectionDialog(orderData.articles);
              if (!selectedArticles) {
                setinfoMessage("Auftrag wurde abgebrochen.");
                return null;
              }

              orderData = {...orderData, articles: selectedArticles}
              skipMultipleArticlesWarning = true;
              continue; // retry
            }
            if (error === "NO_STOCK") {
              const continueAnyway = await showConfirmationDialog(message);
              if (!continueAnyway) {
                setinfoMessage("Auftrag wurde abgebrochen.");
                return null
              }
              skipLagerWarning = true;
              continue; // retry
            }
          }

          // Actual errors
          const error_str = err.response.status === 500
            ? "Interner Serverfehler. Bitte mache einen Screenshot von der Seite und schicke ihn an die Entwickler (oder Jonathan)."
            : json.detail || "Unbekannter Fehler";

          setErrorMessage(error_str);
          return null;
        } else {
          // Network errors
          setErrorMessage(
            "Netzwerkfehler. Deine Internetverbindung ist entweder unterbrochen oder der Server ist nicht erreichbar."
          );
          return null;
        }
      }
    }

    setErrorMessage(
      "Zu viele Versuche. Bitte notiere die OrderID und leite sie an Jonathan weiter."
    );
    return null;
  }

  const handleGoButton = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (selectedLager == null) {
      const err = "Bitte wähle aus, ob es sich um Dropshipping, Kommisionslager oder einen Largerartikel handelt.";
      setErrorMessage(err);
      return;
    }
    if (orderID == null || orderID == "" || orderID.length === 0) {
      const err = "Die Order-ID muss ausgefüllt sein!";
      setinfoMessage("");
      setErrorMessage(err);
      return;
    }

    setPdfUrl(null);
    setinfoMessage("");
    setErrorMessage("");

    let returnedPdfUrl = null;
    if (selectedLager === "Dropshipping") {
      returnedPdfUrl = await processDropshipOrder(orderID);
    } else if (selectedLager === "Kommissionslager") {
      returnedPdfUrl = await processKommissionsOrder(orderID);
    } else if (selectedLager === "Lagerartikel") {
      returnedPdfUrl = await processLagerOrder(orderID);
    } else {
      const err = "Bitte wähle das Lager noch einmal aus.";
      setinfoMessage("");
      setErrorMessage(err);
      return;
    }

    // if (returnedPdfUrl) {
    //   setPdfUrl(returnedPdfUrl);
    //   window.open(returnedPdfUrl, "_blank");
    // }
    if (returnedPdfUrl) {
      // returnedPdfUrl is { url, filename }
      const { url, filename } = returnedPdfUrl;
      setPdfUrl(url);
      setPdfFileName(filename);

      const isPdf = filename.toLowerCase().endsWith(".pdf");

      if (isPdf) {
        // Open viewer tab (note: opened tab cannot read filename from blob URL)
        window.open(url, "_blank");
      }

      // Trigger download with the correct filename (this sets Save dialog filename)
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${orderID}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  return (
    <>
      <Navbar/>
      <Helmet>
        <title>{"Auftragsbearbeitung"}</title>
      </Helmet>
      <h3>Auftragsbearbeitung</h3>
      {renderDefaultOptions()}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: "25px",
          alignItems: "center",
          marginBottom: "15px"
        }}
      >
        <SubmitButton
          onClick={handleGoButton}
          label={"Auftrag bearbeiten"}
          size="small"
          type="submit"
        />
        {pdfUrl && (<DefaultButton label={pdfFileName.toLowerCase().endsWith(".pdf") ? "Öffne PDF" : "Datei herunterladen"}
                                   sx={{width: "130px", minWidth: "130px"}}
                                   onClick={() => {
                                     const isPdf = pdfFileName.toLowerCase().endsWith(".pdf");
                                     if (isPdf) {
                                        // Open PDF in a new browser tab
                                        window.open(pdfUrl, "_blank");
                                     } else {
                                       // Trigger download for ZIP or other non-viewable types
                                       const a = document.createElement("a");
                                       a.href = pdfUrl;
                                       a.download = pdfFileName;
                                       document.body.appendChild(a);
                                       a.click();
                                       a.remove();
                                       }
                                   }}/>)}
      </div>
      {renderErrorMessage()}
      {renderInfoMessage()}
      {ColorModeIcon()}
    </>
  );
}

export default OrderProcessing;
